/** TikTok embed URL helpers shared by Discover and the Audio Studio. */

/** The classic embed page (tiktok.com/embed/v2/<id>) — used by Discover → TikTok trends. */
export function tiktokEmbedV2Url(videoId: string): string {
  return `https://www.tiktok.com/embed/v2/${encodeURIComponent(videoId)}`;
}

/** TikTok's player (tiktok.com/player/v1/<id>) — the lighter video-only embed. */
export function tiktokPlayerUrl(videoId: string): string {
  return `https://www.tiktok.com/player/v1/${encodeURIComponent(videoId)}`;
}

/** True for an https URL on tiktok.com (or a subdomain) — anything else must not be framed. */
export function isTikTokUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && (u.hostname === 'tiktok.com' || u.hostname.endsWith('.tiktok.com'));
  } catch {
    return false;
  }
}

/** The URL to frame for a video: its `embed_url` when that is a tiktok.com URL, else the player URL for its id. */
export function tiktokVideoEmbedUrl(v: { video_id: string; embed_url?: string | null }): string {
  return isTikTokUrl(v.embed_url) ? v.embed_url! : tiktokPlayerUrl(v.video_id);
}

/**
 * The preview embed for grids of cards: always TikTok's video-only player (player/v1 fills a 9:16 box; embed/v2
 * draws its own chrome and letterboxes), controls on, autoplay off, caption / music info / related videos hidden.
 */
export function tiktokPreviewEmbedUrl(v: { video_id: string }, opts: { autoplay?: boolean } = {}): string {
  // Minimal chrome for small cards: keep play + progress, hide volume/fullscreen/timestamp/settings/captions.
  const qs = new URLSearchParams({
    controls: '1', autoplay: opts.autoplay ? '1' : '0', muted: '0', music_info: '0', description: '0', rel: '0',
    progress_bar: '1', play_button: '1', volume_control: '1', fullscreen_button: '0', timestamp: '0',
    closed_caption: '0', native_context_menu: '0',
  });
  return `${tiktokPlayerUrl(v.video_id)}?${qs.toString()}`;
}

/** iframe `allow` list for previews: no autoplay allowance, so a video only starts when the viewer presses play in it. */
export const TIKTOK_PREVIEW_ALLOW = 'encrypted-media; picture-in-picture; fullscreen';

/**
 * `allow` list for a player the viewer just asked for (clicked a poster / opened a song): autoplay is delegated so the
 * video starts immediately — one click, not two. Only ever used for a single, user-started player.
 */
export const TIKTOK_PLAY_ALLOW = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
